/**
 * Pure logic for the account-level display & accessibility preferences — no
 * React, DOM, storage or network — so it is unit-testable under plain Node.
 * The store that owns state, the server round-trip and the <html> attributes
 * is `display-prefs.js`.
 */
import {
  DISPLAY_PREFERENCES,
  LEGACY_FONT_SIZE_LEVELS,
  resolveDisplayPreferences,
} from './display-prefs-defs.js'

/**
 * What a preference set means on screen right now, given what the computer
 * currently asks for (`system`: { prefersDark, prefersReducedMotion,
 * prefersMoreContrast }). "Computer" follows the system value live; On / Off
 * override it. Link underlines have no system signal, so "Computer" keeps the
 * app's own default (not underlined).
 */
export function effectiveDisplay(prefs, system = {}) {
  const p = resolveDisplayPreferences(prefs)
  const follow = (pref, systemOn) => (pref === 'on' ? true : pref === 'off' ? false : Boolean(systemOn))
  return {
    theme: p.theme === 'system' ? (system.prefersDark ? 'dark' : 'light') : p.theme,
    textSize: p.textSize,
    motion: follow(p.reduceMotion, system.prefersReducedMotion) ? 'less' : 'normal',
    contrast: follow(p.contrast, system.prefersMoreContrast) ? 'more' : 'normal',
    underline: p.underlineLinks === 'on' ? 'on' : 'off',
  }
}

/**
 * One-time move of the header text-size choice that used to live only in the
 * browser (per user) into the account. Only when the account has never saved
 * a text size, and only when the browser holds a non-default level — never
 * overwrites a server value. Returns the `display` patch to send, or null.
 */
export function legacyTextSizeMigration(displayUnset, legacyLevel) {
  if (!Array.isArray(displayUnset) || !displayUnset.includes('textSize')) return null
  const mapped = LEGACY_FONT_SIZE_LEVELS[legacyLevel]
  if (!mapped || mapped === DISPLAY_PREFERENCES.textSize.default) return null
  return { textSize: mapped }
}

/**
 * The page's pending edits that still differ from `saved` — an edit that now
 * equals the saved value (because a save, another tab or a header control
 * caught up) is no longer pending. Only touched keys are ever held, so keys
 * the person didn't change always follow the latest saved value. null when
 * nothing is pending.
 */
export function pendingEdits(saved, edits) {
  if (!edits) return null
  const out = {}
  for (const [k, v] of Object.entries(edits)) if (saved?.[k] !== v) out[k] = v
  return Object.keys(out).length ? out : null
}

/** The page sizes every list offers unless it has its own set. */
export const DEFAULT_PAGE_SIZES = Object.freeze([25, 50, 100, 200, 500])

/**
 * The page sizes a list may offer: its own set, cut at the largest page its
 * API accepts (`max`), so the menu never offers a size the server rejects.
 * Always at least one option.
 */
export function pageSizeOptions(options = DEFAULT_PAGE_SIZES, max = Infinity) {
  const capped = options.filter((o) => o <= max)
  return capped.length ? capped : [Math.min(...options)]
}

/**
 * The size a list STARTS at: the person's rows-per-page when the list offers
 * it, else the largest offered size below it (e.g. 500 on a list capped at
 * 100 → 100), else the smallest offered.
 */
export function pageSizeFor(preferred, options = DEFAULT_PAGE_SIZES) {
  if (options.includes(preferred)) return preferred
  const below = options.filter((o) => o <= preferred)
  return below.length ? Math.max(...below) : Math.min(...options)
}

/** Number of keys whose value differs between the saved and previewed sets. */
export function countChanged(saved, preview) {
  if (!preview) return 0
  const a = resolveDisplayPreferences(saved)
  const b = resolveDisplayPreferences(preview)
  return Object.keys(a).filter((k) => a[k] !== b[k]).length
}
